<template>
  <div>
    <loading
      :active.sync="loading"
      :can-cancel="false"
      :is-full-page="true"
      :height="150"
      :width="150"
      :color="'#FFFF00'"
      :background-color="'#000000'"
      :opacity="0.5"
    ></loading>

    <!-- Duplicate warning dialog -->
    <v-dialog v-model="duplicateDialog" max-width="620px" persistent>
      <v-card>
        <v-card-title class="pb-2">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          <span class="warning--text">{{ $t('generatepatternrosterrecord.duplicatewarning') }}</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-3">
          <p class="mb-3">
            {{ $t('generatepatternrosterrecord.duplicatefound1') }}
            <strong>{{ duplicateCount }}</strong>
            {{ $t('generatepatternrosterrecord.duplicatefound2') }}
          </p>
          <v-data-table
            :headers="duplicateHeaders"
            :items="duplicateDetails"
            dense
            :items-per-page="5"
            class="elevation-1"
          >
            <template v-slot:item.shiftdate="{ item }">{{ formatDate(item.shiftdate) }}</template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="duplicateDialog = false">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="warning" text @click="runGenerate('skipstaff')">{{ $t('generatepatternrosterrecord.skipstaff') }}</v-btn>
          <v-btn color="warning" text @click="runGenerate('skipdate')">{{ $t('generatepatternrosterrecord.skipdate') }}</v-btn>
          <v-btn color="error" text @click="runGenerate('overwrite')">{{ $t('generatepatternrosterrecord.overwrite') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Batch detail dialog -->
    <v-dialog v-model="batchDetailDialog" max-width="760px">
      <v-card>
        <v-card-title class="pb-2">
          <v-icon color="white" class="mr-2">mdi-calendar-text</v-icon>
          {{ $t('generatepatternrosterrecord.batchdetailtitle') }}
          <span class="ml-2 text-subtitle-2">({{ batchDetailBatchno }})</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-3">
          <v-data-table
            :headers="batchDetailHeaders"
            :items="batchDetailList"
            dense
            :items-per-page="15"
            class="elevation-1"
          >
            <template v-slot:item.starttime="{ item }">{{ formatTime(item.starttime) }}</template>
            <template v-slot:item.endtime="{ item }">{{ formatTime(item.endtime) }}</template>
            <template v-slot:item.date="{ item }">{{ formatDate(item.date) }}</template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="batchDetailDialog = false">{{ $t('button.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Staff list dialog (for View Staff on batch rows) -->
    <v-dialog v-model="staffDialog" max-width="520px">
      <v-card>
        <v-card-title class="pb-2">
          <v-icon color="white" class="mr-2">mdi-account-group</v-icon>
          {{ $t('generatepatternrosterrecord.stafflisttitle') }}
          <span class="ml-2 text-subtitle-2">({{ staffDialogBatchno }})</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-data-table
            :headers="staffHeaders"
            :items="staffDialogList"
            dense
            :items-per-page="10"
          ></v-data-table>
        </v-card-text>
        <v-card-text class="pt-0 pb-1">
          <p class="grey--text text--darken-1 text-caption font-italic mb-0">
            * {{ $t('generatepatternrosterrecord.viewstaffhint') }}
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="staffDialog = false">{{ $t('button.close') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete batch confirm dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="error--text">
          <v-icon color="error" class="mr-2">mdi-delete-alert</v-icon>
          {{ $t('generatepatternrosterrecord.confirmdeletebatch') }}
        </v-card-title>
        <v-card-text>
          {{ $t('generatepatternrosterrecord.confirmdeletebatchmsg') }}
          <strong>{{ selectedBatch ? selectedBatch.batchno : '' }}</strong>?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">{{ $t('button.cancel') }}</v-btn>
          <v-btn color="error" text @click="confirmDeleteBatch">{{ $t('button.delete') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-container>
      <v-form ref="generateForm">
        <v-row>
          <v-col>
            <h2 class="pb-3">{{ this.parentObject.pageTitle }}</h2>
          </v-col>
        </v-row>

        <!-- ── GENERATE SECTION ── -->
        <v-card outlined class="mb-6">
          <v-card-title class="primary--text text-subtitle-1 font-weight-bold pb-2">
            <v-icon color="primary" class="mr-2">mdi-calendar-plus</v-icon>
            {{ $t('generatepatternrosterrecord.generatesection') }}
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="3" sm="3" class="pb-n5">
                <v-text-field
                  outlined
                  :label="$t('generatepatternrosterrecord.startdate')"
                  type="date"
                  :rules="[rules.required]"
                  v-model="formData.startdate"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="3" sm="3" class="pb-n5">
                <v-text-field
                  outlined
                  :label="$t('generatepatternrosterrecord.enddate')"
                  type="date"
                  :rules="[rules.required]"
                  v-model="formData.enddate"
                  dense
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="5" sm="5" class="pb-n5 mt-n6">
                <v-autocomplete
                  outlined
                  :label="$t('generatepatternrosterrecord.pattern')"
                  :rules="[rules.required]"
                  v-model="formData.patternid"
                  dense
                  :items="patternList"
                  clearable
                  @change="changePatternHandler"
                ></v-autocomplete>
              </v-col>
              <v-col cols="2" sm="2" class="pb-n5 mt-n6">
                <v-text-field
                  outlined
                  :label="$t('generatepatternrosterrecord.commencingdayno')"
                  type="number"
                  :rules="[rules.required, rules.commencingDay]"
                  v-model="formData.commencingdayno"
                  dense
                  min="1"
                  :max="selectedCycledays"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Filter criteria -->
            <v-row>
              <v-col cols="12" class="mt-n4">
                <v-expansion-panels :value="0">
                  <v-expansion-panel>
                    <v-expansion-panel-header class="blue--text">
                      {{ $t('reportsparam.filtercriteria') }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <v-row class="mt-3">
                        <v-col cols="4" sm="4" class="pb-n4 mt-n2">
                          <v-autocomplete outlined :label="$t('columns.company')" v-model="formData.company" dense :items="companyList" clearable></v-autocomplete>
                        </v-col>
                        <v-col cols="4" sm="4" class="pb-n4 mt-n2">
                          <v-autocomplete outlined :label="$t('columns.branch')" v-model="formData.branch" dense :items="branchList" clearable></v-autocomplete>
                        </v-col>
                        <v-col cols="4" sm="4" class="pb-n4 mt-n2">
                          <v-autocomplete outlined :label="$t('columns.department')" v-model="formData.department" dense :items="departmentList" clearable></v-autocomplete>
                        </v-col>
                        <v-col cols="4" sm="4" class="pb-n4 mt-n4">
                          <v-autocomplete outlined :label="$t('columns.division')" v-model="formData.division" dense :items="divisionList" clearable></v-autocomplete>
                        </v-col>
                        <v-col cols="4" sm="4" class="pb-n4 mt-n4">
                          <v-autocomplete outlined :label="$t('columns.section')" v-model="formData.section" dense :items="sectionList" clearable></v-autocomplete>
                        </v-col>
                        <v-col cols="4" sm="4" class="pb-n4 mt-n4">
                          <v-autocomplete outlined :label="$t('columns.unit')" v-model="formData.unit" dense :items="unitList" clearable></v-autocomplete>
                        </v-col>
                        <v-col cols="4" sm="4" class="pb-n4 mt-n4">
                          <v-autocomplete outlined :label="$t('columns.employmenttype')" v-model="formData.employmentType" dense :items="emptypeList" clearable></v-autocomplete>
                        </v-col>
                        <v-col cols="4" sm="4" class="pb-n4 mt-n4">
                          <v-autocomplete outlined :label="$t('periodicincentivesetup.workdaygroup')" v-model="formData.workdaygroup" dense :items="workdaygrouplist" clearable></v-autocomplete>
                        </v-col>
                        <v-col cols="4" sm="4" class="pb-n4 mt-n4">
                          <v-autocomplete outlined :label="$t('columns.job')" v-model="formData.job" dense :items="jobList" clearable></v-autocomplete>
                        </v-col>
                        <v-col cols="4" sm="4" class="pb-n4 mt-n4">
                          <v-autocomplete outlined :label="$t('columns.position')" v-model="formData.position" dense :items="positionList" clearable></v-autocomplete>
                        </v-col>
                        <v-col cols="4" sm="4" class="pb-n4 mt-n4">
                          <v-autocomplete outlined :label="$t('columns.staff')" v-model="formData.staff" dense :items="staffDetailsList" clearable></v-autocomplete>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>

            <v-row class="mt-4">
              <v-col>
                <v-btn color="primary" @click="onGenerate" style="width: 160px">
                  <v-icon class="mr-1">mdi-cog-play</v-icon>
                  {{ $t('generatepatternrosterrecord.generatebtn') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- ── DELETE BATCH SECTION ── -->
        <v-card outlined>
          <v-card-title class="error--text text-subtitle-1 font-weight-bold pb-2">
            <v-icon color="error" class="mr-2">mdi-delete-sweep</v-icon>
            {{ $t('generatepatternrosterrecord.deletebatchsection') }}
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="batchHeaders"
              :items="batches"
              class="elevation-1"
              :sort-by="['rectimestamp']"
              :sort-desc="[true]"
            >
              <template v-slot:item.action="{ item }">
                <div class="gv-row-actions">
                  <button type="button" class="gv-action-btn gv-action-edit" :title="$t('generatepatternrosterrecord.viewstaff')" @click="viewBatchStaff(item)">
                    <v-icon size="15">mdi-pencil</v-icon>
                  </button>
                  <button type="button" class="gv-action-btn gv-action-view" :title="$t('generatepatternrosterrecord.viewbatchdetail')" @click="viewBatchDetail(item)">
                    <v-icon size="15">mdi-calendar-text</v-icon>
                  </button>
                  <button type="button" class="gv-action-btn gv-action-delete" :title="$t('button.delete')" @click="promptDeleteBatch(item)">
                    <v-icon size="15">mdi-delete</v-icon>
                  </button>
                </div>
              </template>
              <template v-slot:item.startdate="{ item }">{{ formatDate(item.startdate) }}</template>
              <template v-slot:item.enddate="{ item }">{{ formatDate(item.enddate) }}</template>
              <template v-slot:item.rectimestamp="{ item }">{{ formatDateTime(item.rectimestamp) }}</template>
              <template v-slot:item.filtercompany="{ item }">{{ resolveFilter(companyList, item.filtercompany) }}</template>
              <template v-slot:item.filterbranch="{ item }">{{ resolveFilter(branchList, item.filterbranch) }}</template>
              <template v-slot:item.filterdivision="{ item }">{{ resolveFilter(divisionList, item.filterdivision) }}</template>
              <template v-slot:item.filterdepartment="{ item }">{{ resolveFilter(departmentList, item.filterdepartment) }}</template>
              <template v-slot:item.filtersection="{ item }">{{ resolveFilter(sectionList, item.filtersection) }}</template>
              <template v-slot:item.filterunit="{ item }">{{ resolveFilter(unitList, item.filterunit) }}</template>
              <template v-slot:item.filteremptype="{ item }">{{ resolveFilter(emptypeList, item.filteremptype) }}</template>
              <template v-slot:item.filterworkdaygroup="{ item }">{{ resolveFilter(workdaygrouplist, item.filterworkdaygroup) }}</template>
              <template v-slot:item.filterjob="{ item }">{{ resolveFilter(jobList, item.filterjob) }}</template>
              <template v-slot:item.filterposition="{ item }">{{ resolveFilter(positionList, item.filterposition) }}</template>
              <template v-slot:item.filterstaff="{ item }">{{ resolveFilter(staffDetailsList, item.filterstaff) }}</template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-form>
    </v-container>

    <div v-if="isErr">
      <app-error
        v-bind:parentError="{ data: this.errMsg, isErr: this.isErr }"
        v-on:closeError="isErr = $event"
      />
    </div>

    <div v-if="isSuccess">
      <app-success
        v-bind:parentObject="{ isSuccess: this.isSuccess, messageSuccess: this.successMsg }"
        v-on:close-success="isSuccess = $event"
      />
    </div>
  </div>
</template>

<script>
import ErrorDialog from "../../common/ErrorDialog";
import SuccessDialog from "../../common/SuccessDialog";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import moment from "moment";

export default {
  props: { parentObject: Object },

  components: {
    "app-error": ErrorDialog,
    "app-success": SuccessDialog,
    Loading,
  },

  data() {
    return {
      rules: {
        required: v => !!v || this.$t('reportsparam.required'),
        commencingDay: v => {
          const n = parseInt(v);
          return (n >= 1 && n <= this.selectedCycledays) || this.$t('generatepatternrosterrecord.commencingdayrange');
        },
      },
      formData: {
        startdate: moment().format('YYYY-MM-DD'),
        enddate: moment().format('YYYY-MM-DD'),
        patternid: null,
        commencingdayno: 1,
        company: null,
        branch: null,
        division: null,
        department: null,
        section: null,
        unit: null,
        employmentType: null,
        workdaygroup: null,
        job: null,
        position: null,
        staff: null,
        userid: this.$store.getters.userId,
      },
      selectedCycledays: 99,
      patternItems: [],
      patternList: [],
      batches: [],
      batchHeaders: [
        { text: this.$t('columns.actions'), value: 'action', width: '95px', sortable: false },
        { text: this.$t('generatepatternrosterrecord.batchno'), value: 'batchno', width: '180px' },
        { text: this.$t('generatepatternrosterrecord.pattern'), value: 'patternname', width: '180px' },
        { text: this.$t('generatepatternrosterrecord.startdate'), value: 'startdate', width: '120px' },
        { text: this.$t('generatepatternrosterrecord.enddate'), value: 'enddate', width: '120px' },
        { text: this.$t('generatepatternrosterrecord.staffcount'), value: 'totalstaff', width: '100px' },
        { text: this.$t('generatepatternrosterrecord.generateddate'), value: 'rectimestamp', width: '160px' },
        { text: this.$t('columns.company'), value: 'filtercompany', width: '140px' },
        { text: this.$t('columns.branch'), value: 'filterbranch', width: '140px' },
        { text: this.$t('columns.division'), value: 'filterdivision', width: '140px' },
        { text: this.$t('columns.department'), value: 'filterdepartment', width: '140px' },
        { text: this.$t('columns.section'), value: 'filtersection', width: '140px' },
        { text: this.$t('columns.unit'), value: 'filterunit', width: '140px' },
        { text: this.$t('columns.employmenttype'), value: 'filteremptype', width: '150px' },
        { text: this.$t('periodicincentivesetup.workdaygroup'), value: 'filterworkdaygroup', width: '150px' },
        { text: this.$t('columns.job'), value: 'filterjob', width: '140px' },
        { text: this.$t('columns.position'), value: 'filterposition', width: '140px' },
        { text: this.$t('columns.staff'), value: 'filterstaff', width: '180px' },
      ],
      staffHeaders: [
        { text: this.$t('columns.no'), value: 'no', width: '60px', sortable: false },
        { text: this.$t('columns.staffcode'), value: 'staffcode', width: '140px' },
        { text: this.$t('columns.name'), value: 'psnname' },
      ],
      staffDialog: false,
      staffDialogList: [],
      staffDialogBatchno: '',
      batchDetailDialog: false,
      batchDetailBatchno: '',
      batchDetailList: [],
      batchDetailHeaders: [
        { text: this.$t('generatepatternrosterrecord.rosterdate'), value: 'date', width: '120px' },
        { text: this.$t('generatepatternrosterrecord.commencingdayno'), value: 'dayno', width: '80px' },
        { text: this.$t('setuppattern.daytype'), value: 'daytypedesc', width: '120px' },
        { text: this.$t('setuppattern.shiftcode'), value: 'shiftcode', width: '110px' },
        { text: this.$t('setuppattern.shiftdesc'), value: 'shiftdesc' },
        { text: this.$t('setupforshift.starttime'), value: 'starttime', width: '110px' },
        { text: this.$t('setupforshift.endtime'), value: 'endtime', width: '110px' },
      ],
      duplicateHeaders: [
        { text: this.$t('columns.staffcode'), value: 'staffcode', width: '130px' },
        { text: this.$t('columns.name'), value: 'psnname' },
        { text: this.$t('generatepatternrosterrecord.rosterdate'), value: 'shiftdate', width: '130px' },
      ],
      duplicateDialog: false,
      duplicateCount: 0,
      duplicateDetails: [],
      deleteDialog: false,
      selectedBatch: null,
      loading: false,
      isErr: false,
      errMsg: '',
      isSuccess: false,
      successMsg: '',
      companyList: [],
      branchList: [],
      divisionList: [],
      departmentList: [],
      sectionList: [],
      unitList: [],
      emptypeList: [],
      workdaygrouplist: [],
      jobList: [],
      positionList: [],
      staffDetailsList: [],
    };
  },

  methods: {
    changePatternHandler(id) {
      const item = this.patternItems.find(p => p.id == id);
      this.selectedCycledays = item ? item.cycledays : 99;
      this.formData.commencingdayno = 1;
    },

    hasAtLeastOneFilter() {
      const f = this.formData;
      return !!(f.company || f.branch || f.division || f.department ||
                f.section || f.unit || f.employmentType || f.workdaygroup ||
                f.job || f.position || f.staff);
    },

    onGenerate() {
      if (!this.$refs.generateForm.validate()) return;
      if (!this.hasAtLeastOneFilter()) {
        this.isErr = true;
        this.errMsg = this.$t('generatepatternrosterrecord.filterrequired');
        return;
      }
      if (!this.CheckPermission(this.$route.meta.funcname, 'canadd')) return;

      this.loading = true;
      this.$store.dispatch('post', { url: '/patternrostergen/check', payload: this.formData })
        .then(res => {
          this.loading = false;
          if (res.data.status !== 200) {
            this.isErr = true;
            this.errMsg = res.data.error;
            return;
          }
          if (res.data.duplicates > 0) {
            this.duplicateCount = res.data.duplicates;
            this.duplicateDetails = res.data.duplicateDetails || [];
            this.duplicateDialog = true;
          } else {
            this.runGenerate('skip');
          }
        })
        .catch(err => { this.loading = false; this.isErr = true; this.errMsg = err; });
    },

    runGenerate(duplicateAction) {
      this.duplicateDialog = false;
      this.loading = true;
      const payload = Object.assign({}, this.formData, { duplicateAction });
      this.$store.dispatch('post', { url: '/patternrostergen/generate', payload })
        .then(res => {
          this.loading = false;
          if (res.data.status === 201) {
            this.loadBatches();
            this.isSuccess = true;
            this.successMsg = `${this.$t('generatepatternrosterrecord.generatedsuccess')} ${res.data.batchno}`;
          } else {
            this.isErr = true;
            this.errMsg = res.data.error;
          }
        })
        .catch(err => { this.loading = false; this.isErr = true; this.errMsg = err; });
    },

    viewBatchDetail(batch) {
      this.$store.dispatch('get', { url: `/patternrosterbatch/${batch.id}/detail` })
        .then(res => {
          if (res.data.status === 200) {
            this.batchDetailBatchno = batch.batchno;
            this.batchDetailList = res.data.rec;
            this.batchDetailDialog = true;
          } else {
            this.isErr = true;
            this.errMsg = res.data.error;
          }
        })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    viewBatchStaff(batch) {
      this.$store.dispatch('get', { url: `/patternrosterbatch/${batch.id}/staff` })
        .then(res => {
          if (res.data.status === 200) {
            this.staffDialogBatchno = batch.batchno;
            this.staffDialogList = (res.data.rec || []).map((s, i) => ({ ...s, no: i + 1 }));
            this.staffDialog = true;
          } else {
            this.isErr = true;
            this.errMsg = res.data.error;
          }
        })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    promptDeleteBatch(item) {
      this.selectedBatch = item;
      this.deleteDialog = true;
    },

    confirmDeleteBatch() {
      if (!this.CheckPermission(this.$route.meta.funcname, 'candelete')) return;
      this.deleteDialog = false;
      this.loading = true;
      this.$store.dispatch('delete', { url: `/patternrosterbatch/${this.selectedBatch.id}` })
        .then(res => {
          this.loading = false;
          if (res.data.status === 200) {
            this.loadBatches();
          } else {
            this.isErr = true;
            this.errMsg = res.data.error;
          }
        })
        .catch(err => { this.loading = false; this.isErr = true; this.errMsg = err; });
    },

    loadBatches() {
      this.$store.dispatch('get', { url: '/patternrosterbatch/all' })
        .then(res => { this.batches = res.data.rec || []; })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    loadPatterns() {
      this.$store.dispatch('get', { url: '/patternsetup/all' })
        .then(res => {
          this.patternItems = res.data.rec.filter(p => !p.disable);
          this.patternList = this.patternItems.map(p => ({ text: `${p.code} - ${p.name}`, value: p.id }));
        })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    loadCompanies() {
      this.$store.dispatch('get', { url: '/company/all' })
        .then(res => { this.companyList = res.data.rec.map(r => ({ text: r.compnyname, value: r.compnycode })); })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    loadBranches() {
      this.$store.dispatch('get', { url: '/branch/all' })
        .then(res => { this.branchList = res.data.rec.map(r => ({ text: r.brchname, value: r.brchcode })); })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    loadDivisions() {
      this.$store.dispatch('get', { url: '/division/all' })
        .then(res => { this.divisionList = res.data.rec.map(r => ({ text: r.divnname, value: r.divncode })); })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    loadDepartments() {
      this.$store.dispatch('get', { url: '/department/all' })
        .then(res => { this.departmentList = res.data.rec.map(r => ({ text: r.deptname, value: r.deptcode })); })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    loadSections() {
      this.$store.dispatch('get', { url: '/section/all' })
        .then(res => { this.sectionList = res.data.rec.map(r => ({ text: r.sectioname, value: r.sectiocode })); })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    loadUnits() {
      this.$store.dispatch('get', { url: '/unit/all' })
        .then(res => { this.unitList = res.data.rec.map(r => ({ text: r.unitname, value: r.unitcode })); })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    loadEmpTypes() {
      this.$store.dispatch('get', { url: '/emptype/all' })
        .then(res => { this.emptypeList = res.data.rec.map(r => ({ text: r.emptypedesc, value: r.emptype })); })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    loadWorkdayGroups() {
      this.$store.dispatch('get', { url: '/groupp_grouppb_workday/all' })
        .then(res => { this.workdaygrouplist = res.data.rec.map(r => ({ text: r.valuedesc, value: r.valvalueid })); })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    loadJobs() {
      this.$store.dispatch('get', { url: '/job/all' })
        .then(res => { this.jobList = res.data.rec.map(r => ({ text: `${r.jobcode} - ${r.jobtitle}`, value: r.jobcode })); })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    loadPositions() {
      this.$store.dispatch('get', { url: '/position/all' })
        .then(res => { this.positionList = res.data.rec.map(r => ({ text: `${r.poscode} - ${r.posdesc}`, value: r.poscode })); })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    loadStaff() {
      this.$store.dispatch('get', { url: `/staff/all/${this.$store.getters.userId}` })
        .then(res => { this.staffDetailsList = res.data.rec.map(r => ({ text: r.psndetails, value: r.id })); })
        .catch(err => { this.isErr = true; this.errMsg = err; });
    },

    resolveFilter(list, value) {
      if (!value) return '-';
      const match = list.find(i => i.value == value);
      return match ? match.text : value;
    },
    formatDate(val) { return val ? moment(val).format('DD/MM/YYYY') : ''; },
    formatTime(val) { return val ? moment(val, 'HH:mm:ss').format('HH:mm') : '-'; },
    formatDateTime(val) { return val ? moment(val).format('DD/MM/YYYY HH:mm') : ''; },
  },

  mounted() {
    this.loadBatches();
    this.loadPatterns();
    this.loadCompanies();
    this.loadBranches();
    this.loadDivisions();
    this.loadDepartments();
    this.loadSections();
    this.loadUnits();
    this.loadEmpTypes();
    this.loadWorkdayGroups();
    this.loadJobs();
    this.loadPositions();
    this.loadStaff();
  },
};
</script>

<style scoped>
.gv-row-actions { display: flex; align-items: center; gap: 4px; }
.gv-action-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; border-radius: 5px; border: none;
  cursor: pointer; transition: all 0.12s; background: transparent;
}
.gv-action-edit   { color: #64748B; }
.gv-action-delete { color: #64748B; }
.gv-action-edit:hover   { background: #EEF2FF; color: #4F6BFF; }
.gv-action-delete:hover { background: #FEE2E2; color: #DC2626; }
</style>
